import React, { Dispatch, FC, useState } from "react";
import styled from "styled-components";
import { ExternalAppApiType } from "../../util/types";
import {
  LoadingBar,
  TextInput,
  Typography,
  TypographyVariant,
} from "../ui-kit";
import { TagEntry } from "./components/TagEntry";
import { useAddTagToEntity } from "./hooks/useAddTagToEntity";
import { useRemoveTagFromEntity } from "./hooks/useRemoveTagFromEntity";

const TagsBlock = styled.div<{ isB2bTagSettings?: boolean }>`
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 10px;
  box-sizing: border-box;
  padding: 20px;
  @media (max-width: 900px) {
    padding: 10px;
  }
  ${({ isB2bTagSettings }) =>
    isB2bTagSettings && `padding: 0; border: none; border-radius: 0;`}
`;

const TopLineCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 7px;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  align-items: baseline;
`;

const EditBtn = styled.button`
  border: none;
  background-color: transparent;
  color: #027aff;
  text-align: center;
  font-family: "Montserrat", sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 13px;
  line-height: 22px;

  :hover {
    text-decoration: underline;
    cursor: pointer;
  }
  :disabled {
    text-decoration: none;
    color: #adb5bd;
    pointer-events: none;
  }
`;

const TagEntriesWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

export enum TagManagerEntityTypes {
  ORDER = "order",
  CUSTOMER = "customer",
  APP_ORDER_TAGS = "appOrderTags",
  APP_CUSTOMER_TAGS = "appCustomerTags",
  APP_SETUP_WHOLESALE_TAGS = "appSetupWholesaleTags",
}

interface ThisComponentProps {
  entityType: TagManagerEntityTypes;
  entityId: string;
  tags?: string[];
  className?: string;
  isB2bTagSettings?: boolean;
  appData?: ExternalAppApiType;
  externalSetTagsFunction?: Dispatch<React.SetStateAction<string[]>>;
}

export const TagManager: FC<ThisComponentProps> = ({
  entityType,
  entityId,
  tags,
  className,
  isB2bTagSettings,
  appData,
  externalSetTagsFunction,
}) => {
  const [tagInputValue, setTagInputValue] = useState("");
  const [tagError, setTagError] = useState<string>();
  const { addTagToEntity, isLoading: isAddingTag } = useAddTagToEntity();
  const { removeTagFromEntity, isLoading: isRemovingTag } =
    useRemoveTagFromEntity();

  // handlers and helpers
  const validateTag = (tag: string): boolean => {
    if (!tag.trim()) {
      setTagError("Tag cannot be empty");
      return false;
    }
    if (
      tags
        ?.map((el) => el.trim().toLowerCase())
        ?.includes(tag.trim().toLowerCase())
    ) {
      setTagError("This tag already exists");
      return false;
    }
    setTagError(undefined);
    return true;
  };

  const handleAddTag = async () => {
    if (!validateTag(tagInputValue)) return;
    entityType !== TagManagerEntityTypes.APP_SETUP_WHOLESALE_TAGS &&
      (await addTagToEntity({
        entityType,
        entityId,
        tag: tagInputValue.trim(),
        errorCallback: setTagError,
        appData,
      }));

    entityType === TagManagerEntityTypes.APP_SETUP_WHOLESALE_TAGS &&
      externalSetTagsFunction?.((prev) => [
        ...prev,
        tagInputValue.trim().toLowerCase(),
      ]);

    setTagInputValue("");
  };
  // End handlers and helpers

  return (
    <TagsBlock className={className} isB2bTagSettings={isB2bTagSettings}>
      {!isB2bTagSettings ? (
        <>
          <TopLineCard>
            <Typography variant={TypographyVariant.HEADER2}>Tags</Typography>
            <EditBtn
              onClick={handleAddTag}
              disabled={isAddingTag || isRemovingTag}
            >
              + Add tag
            </EditBtn>
          </TopLineCard>
          <TextInput
            placeholder="channel, etc"
            value={tagInputValue}
            onChange={(e: any) => {
              setTagInputValue(e.target.value);
              validateTag(e.target.value);
            }}
            error={tagError}
            disabled={isAddingTag || isRemovingTag}
            onBlur={() => {
              if (tagInputValue === "") {
                setTagError(undefined);
              }
            }}
          />
        </>
      ) : (
        <>
          <Row>
            <div style={{ width: 250 }}>
              <TextInput
                placeholder="channel, etc"
                value={tagInputValue}
                onChange={(e: any) => {
                  setTagInputValue(e.target.value);
                  validateTag(e.target.value);
                }}
                error={tagError}
                disabled={isAddingTag || isRemovingTag}
                onBlur={() => {
                  if (tagInputValue === "") {
                    setTagError(undefined);
                  }
                }}
              />
            </div>
            <EditBtn
              onClick={handleAddTag}
              disabled={isAddingTag || isRemovingTag}
            >
              + Add tag
            </EditBtn>
          </Row>
        </>
      )}
      <LoadingBar loading={isRemovingTag || isAddingTag} margin="7px 0 -10px" />
      <TagEntriesWrapper>
        {tags?.map((tag, index) => (
          <TagEntry
            key={"tag-" + index.toString() + tag.toString()}
            tag={tag}
            onRemoveTag={async () => {
              entityType !== TagManagerEntityTypes.APP_SETUP_WHOLESALE_TAGS &&
                (await removeTagFromEntity({
                  entityType,
                  entityId,
                  tag,
                  errorCallback: setTagError,
                  appData,
                }));

              entityType === TagManagerEntityTypes.APP_SETUP_WHOLESALE_TAGS &&
                externalSetTagsFunction?.((prev) =>
                  prev.filter(
                    (t) => t.trim().toLowerCase() !== tag.trim().toLowerCase()
                  )
                );
            }}
            disabled={isAddingTag || isRemovingTag}
          />
        ))}
      </TagEntriesWrapper>
    </TagsBlock>
  );
};
